import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { TextField, InputAdornment, Typography, Button } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';

// UTILITY
import { formatPrice } from '@/utility/currency';
import { getPlan } from '@/utility/subscription';

// STORE
import { NotifActions } from '@/store/actions';

// STYLES
import { AutocompleteWrapper, ListItem, NoResults } from './styles';

const MultiselectSearch = ({
  t,
  tReady,
  name,
  placeholder,
  data,
  selected: selectedData,
  selectedAddons,
  onChange,
  onSeeChannelsClick,
  InputProps,
  onAddonСlick,
  onOpenCallback,
  ...restProps
}) => {
  const [open, setOpen] = useState(false);
  const [searchString, setSearchString] = useState('');
  const dispatch = useDispatch();
  const selectedCount = selectedData.length;

  function pushNotification(params) {
    dispatch(NotifActions.pushNotification.action(params));
  }

  const handleOpen = () => {
    setOpen(true);
    if (onOpenCallback) onOpenCallback();
  };

  const handleClose = (e, reason) => {
    if (e?.keyCode === 13 && (reason === 'reset' || reason === 'create-option')) {
      return;
    }

    setOpen(false);
    setSearchString('');
  };

  const handleListItem = (event, value) => {
    event.stopPropagation();
    const plan = getPlan(value);
    const hasExtras = !!value?.addOns?.length;
    let reason = event.target?.dataset?.reason;

    if (!reason) {
      const optionId = event.target.getAttribute('aria-activedescendant');
      const optionElement = document.getElementById(optionId);

      // CHECK FOR EXISTING OPTIONELEMENT
      if (!optionElement) {
        return;
      }

      const optionElementChildNode = optionElement.childNodes[0];
      reason = optionElementChildNode?.dataset?.reason;
    }

    if (reason === 'disabled-option') {
      pushNotification({
        message: t('common.includedIn', { name: value.included_in }),
        options: {
          variant: 'success',
        },
      });

      return;
    }

    let array = hasExtras && reason === 'select-option' ? [] : [...selectedData];

    if (reason === 'remove-option') {
      const selectedIndex = selectedData.indexOf(plan.id);
      array.splice(selectedIndex, 1);

      onAddonСlick({
        addon_id: value.id,
        addon_name: value.name,
        addon_price: value.plans[0].price,
        addon_plan_id: value.plans[0].id,
        add_or_remove: 'remove',
      });
    }

    if (reason === 'select-option') {
      array.push(plan.id);

      if (hasExtras) {
        const extrasAddons = value.addOns;
        const existingExtras = data.filter(itemData => extrasAddons.includes(itemData.id));
        const existingExtrasIds = existingExtras.map(existingExtra => {
          const extPlan = getPlan(existingExtra);

          return extPlan?.id || '';
        });

        if (existingExtrasIds.length) array = [...existingExtrasIds, ...array];
      }

      onAddonСlick({
        addon_id: value.id,
        addon_name: value.name,
        addon_price: value.plans[0].price,
        addon_plan_id: value.plans[0].id,
        add_or_remove: 'add',
      });
    }

    onChange(name, array);
  };

  const onInputChange = (event, value, reason) => {
    if (reason === 'reset') {
      return;
    }

    if (event?.keyCode === 13 && reason === 'reset') {
      return;
    }

    let newInputValue = value;
    if (reason === 'reset' || reason === 'clear') {
      newInputValue = '';
    }

    setSearchString(newInputValue);
  };

  const getPrice = (price, interval) => {
    return price && Math.ceil(price) !== 0 ? (
      <>
        ${formatPrice(+price)}
        <Typography className="p5" component="span" color="textSecondary">
          /{interval}
        </Typography>
      </>
    ) : (
      t('common.free')
    );
  };

  return (
    <AutocompleteWrapper className="multi-select-search">
      <Autocomplete
        open={open}
        {...restProps}
        freeSolo
        inputValue={searchString}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={handleListItem}
        onInputChange={onInputChange}
        options={data}
        getOptionLabel={option => `${option.name}` || ''}
        ListboxComponent="ul"
        forcePopupIcon={false}
        value=""
        openOnFocus
        disableClearable
        disablePortal
        disableCloseOnSelect
        renderOption={option => {
          const plan = getPlan(option);
          const selected = selectedData.includes(plan.id);
          const channelsCount = option.channels.length;
          const isIncluded =
            selected &&
            selectedAddons.some(item => item.addOns.length && item.addOns.includes(option.id));

          function getReason() {
            let reason = selected ? 'remove-option' : 'select-option';

            if (isIncluded) {
              reason = 'disabled-option';
            }

            return reason;
          }

          return (
            <ListItem
              className={clsx(
                'multi-select-search__option list-item',
                isIncluded && 'list-item--disabled',
              )}
              data-reason={getReason()}
              selected={selected}
              have={option.list_status}
              key={`${name}-option-${option.id}-${option.name}`}
            >
              {selected ? (
                <CheckBoxIcon className="list-item__icon" />
              ) : (
                <CheckBoxOutlineBlankIcon className="list-item__icon" />
              )}

              <div className="list-item__info">
                {/* {!!option.img && ( */}
                {/*  <div className="list-item__img"> */}
                {/*    <img src={option.img} alt={option.name} /> */}
                {/*  </div> */}
                {/* )} */}
                <Typography
                  className={clsx('list-item__title', !selected && 'p2')}
                  component="p"
                  variant="h5"
                >
                  {option.name}
                </Typography>
                {option.has_trial && (
                  <Typography className="p5 text-color--purple">
                    {t('common.trialDaysPeriod', { period: option.trial_days })}
                  </Typography>
                )}
              </div>

              {channelsCount ? (
                <Typography className="p5" color="textSecondary">
                  {channelsCount} {t(`common.${channelsCount === 1 ? 'channel' : 'channels'}`)}
                </Typography>
              ) : (
                <div className="list-item__dummy-channels-count" />
              )}

              <Typography className="list-item__price" component="p" variant="h6" color="secondary">
                {getPrice(plan.price, plan.interval)}
              </Typography>

              {option.channels.length ? (
                <Button
                  className="list-item__btn"
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={() => onSeeChannelsClick(true, option)}
                >
                  {t('buttons.seeChannels')}
                </Button>
              ) : (
                <div className="list-item__dummy-btn" />
              )}
            </ListItem>
          );
        }}
        renderInput={params => {
          return (
            <TextField
              {...params}
              variant="filled"
              className="multi-select-search__input"
              placeholder={placeholder}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <InputAdornment
                    className={clsx(
                      'multi-select-search__input-adornment',
                      open && 'multi-select-search__input-adornment--open',
                    )}
                    position="start"
                  >
                    {!!selectedCount && (
                      <Typography className="m-r-15" color="primary">
                        {t('common.countAdded', { count: selectedCount })}
                      </Typography>
                    )}
                    <KeyboardArrowDownIcon
                      className="multi-select-search__chevron"
                      color="secondary"
                      fontSize="inherit"
                    />
                  </InputAdornment>
                ),
              }}
            />
          );
        }}
        noOptionsText={<NoResults>{t('searchAutoComplete.noResults')}</NoResults>}
      />
    </AutocompleteWrapper>
  );
};

MultiselectSearch.defaultProps = {
  data: [],
  selected: [],
  selectedAddons: [],
  InputProps: { endAdornment: null },
  onSeeChannelsClick: () => {},
};

MultiselectSearch.propTypes = {
  t: PropTypes.func.isRequired,
  tReady: PropTypes.bool.isRequired,
  data: PropTypes.array,
  selected: PropTypes.array,
  selectedAddons: PropTypes.array,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onSeeChannelsClick: PropTypes.func,
  InputProps: PropTypes.object,
  onAddonСlick: PropTypes.func,
  onOpenCallback: PropTypes.func,
};

export default React.memo(withTranslation()(MultiselectSearch));
